"use client";

import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Toaster } from "@/components/ui/toaster";
import { ActionButtons } from "./action-buttons";
import { BookingTable } from "./booking-table";
import { GameInfoSection } from "./game-info-section";
import { NavigationMenu } from "./navigation-menu";
import { ScrollingAnnouncementBar } from "./scrolling-announcement-bar";
import { apiService, Session } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";

interface LotteryDashboardProps {
  username: string;
  userRole: "user" | "admin";
}

interface TableRow {
  group: string;
  inputs: string[];
  qty: number;
  amt: number;
  result: string;
}

export function LotteryDashboard({ username, userRole }: LotteryDashboardProps) {
  const { user, isAdmin } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState("Booking - Choice");
  const [selectedGameTime, setSelectedGameTime] = useState("Morning (13:00)");
  const [sessions, setSessions] = useState<Session[]>([]);
  const [activeSession, setActiveSession] = useState<Session | null>(null);
  const [balance, setBalance] = useState(0);
  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const tokenFromUrl: string | null = searchParams.get("token");
    if (tokenFromUrl) {
      localStorage.setItem("auth_token", tokenFromUrl);
    }
  }, [searchParams, navigate]);

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);

      // Get active session
      const sessionResponse = await apiService.getActiveSession();
      if (sessionResponse.success && sessionResponse.data) {
        setActiveSession(sessionResponse.data);
        setSelectedGameTime(`${sessionResponse.data.name} (${sessionResponse.data.drawTime})`);
      }

      // Get user balance
      const balanceResponse = await apiService.getBalance();
      if (balanceResponse.success && balanceResponse.data) {
        setBalance(balanceResponse.data.balance);
      }

      // Get all sessions (for admin)
      if (isAdmin) {
        const sessionsResponse = await apiService.getAllSessions();
        if (sessionsResponse.success && sessionsResponse.data) {
          setSessions(sessionsResponse.data);
        }
      }

      // Initialize table based on selected game
      initializeTableData("Booking - Choice");
    } catch (error) {
      console.error("Error fetching initial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const initializeTableData = (gameType: string) => {
    if (gameType === "Dear Single") {
      setTableData([
        { group: "DEAR-1st", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
        { group: "DEAR-2nd", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
        { group: "DEAR-3rd", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
        { group: "DEAR-4th", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
        { group: "DEAR-5th Last", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
        { group: "All", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
      ]);
    } else if (gameType === "Rajshree Punjab") {
      setTableData([
        { group: "RAJSHREE-1st", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
        { group: "RAJSHREE-2nd", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
        { group: "RAJSHREE-3rd", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
        { group: "RAJSHREE-4th", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
        { group: "RAJSHREE-5th", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
        { group: "Last", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
        { group: "All", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
      ]);
    } else {
      // Choice game
      setTableData([
        { group: "Group 1", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
        { group: "Group 2", inputs: Array(10).fill(""), qty: 0, amt: 0, result: "-" },
      ]);
    }
  };

  const handleInputChange = (rowIndex: number, colIndex: number, value: string) => {
    // Only allow single digits
    if (value.length > 1 || (value && !/^\d$/.test(value))) return;

    setTableData((prev) => {
      const newData = [...prev];
      newData[rowIndex].inputs[colIndex] = value;

      // Calculate quantity and amount based on filled inputs
      const filledInputs = newData[rowIndex].inputs.filter((input: string) => input !== "").length;
      newData[rowIndex].qty = filledInputs > 0 ? filledInputs : 0;
      newData[rowIndex].amt = filledInputs > 0 ? filledInputs * 5 : 0;

      return newData;
    });
  };

  // Handle game type change
  useEffect(() => {
    initializeTableData(selectedGame);
  }, [selectedGame]);

  const handleBuy = async () => {
    if (!activeSession) {
      alert("No active session found!");
      return;
    }

    try {
      setLoading(true);

      // Process based on game type
      if (selectedGame === "Dear Single") {
        await processSingleBets();
      } else if (selectedGame === "Rajshree Punjab") {
        await processRajshreeBets();
      } else {
        await processChoiceBets();
      }

      // Refresh balance
      const balanceResponse = await apiService.getBalance();
      if (balanceResponse.success && balanceResponse.data) {
        setBalance(balanceResponse.data.balance);
      }

      alert("Bets placed successfully!");
      handleClear();
    } catch (error) {
      console.error("Error placing bets:", error);
      alert("Failed to place bets. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const processSingleBets = async () => {
    if (!activeSession) return;

    for (const row of tableData) {
      const numbers = row.inputs.filter((input) => input !== "");
      for (const number of numbers) {
        const booking = {
          sessionId: activeSession.id,
          number: parseInt(number),
          qty: 1,
          amount: 5, // Fixed amount per number
        };
        await apiService.createSingleBooking(booking);
      }
    }
  };

  const processRajshreeBets = async () => {
    if (!activeSession) return;

    for (const row of tableData) {
      const numbers = row.inputs.filter((input) => input !== "");
      for (const number of numbers) {
        const series = row.group.includes("RAJSHREE") ? "RS" : "LS"; // Example series
        const booking = {
          sessionId: activeSession.id,
          series: series,
          number: parseInt(number),
          qty: 1,
          amount: 5, // Fixed amount per number
        };
        await apiService.createRajshreeBooking(booking);
      }
    }
  };

  const processChoiceBets = async () => {
    if (!activeSession) return;

    for (const row of tableData) {
      const numbers = row.inputs.filter((input) => input !== "");
      if (numbers.length > 0) {
        const booking = {
          sessionId: activeSession.id,
          numbers: numbers.join(","),
          qty: 1,
          amount: row.amt,
        };
        await apiService.createChoiceBooking(booking);
      }
    }
  };

  const handleClear = () => {
    setTableData((prev) =>
      prev.map((row) => ({
        ...row,
        inputs: Array(10).fill(""),
        qty: 0,
        amt: 0,
      }))
    );
  };

  const handleCancel = () => {
    handleClear();
  };

  const handleSingleResult = async () => {
    if (!activeSession || !isAdmin) {
      alert("Admin access required to run draws");
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.createDraw(activeSession.id);
      if (response.success) {
        alert("Draw completed successfully!");
        await fetchInitialData(); // Refresh all data
      } else {
        alert("Failed to run draw: " + response.error);
      }
    } catch (error) {
      console.error("Error running draw:", error);
      alert("Failed to run draw");
    } finally {
      setLoading(false);
    }
  };

  const handleSingleReport = async () => {
    if (!activeSession || !isAdmin) {
      alert("Admin access required to view reports");
      return;
    }

    try {
      setLoading(true);
      let reportType: "single" | "rajshree" | "choice" = "single";

      if (selectedGame === "Rajshree Punjab") {
        reportType = "rajshree";
      } else if (selectedGame === "Booking - Choice") {
        reportType = "choice";
      }

      // const response = await apiService.getReports(activeSession.id, reportType)
      // if (response.success) {
      //   console.log("Report data:", response.data)
      //   alert(`Report generated for ${selectedGame}. Check console for details.`)
      // } else {
      //   alert("Failed to generate report: " + response.error)
      // }
    } catch (error) {
      console.error("Error generating report:", error);
      alert("Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  // Update header to show real balance
  const headerText = `Three Circle 5 - Balance: ₹${balance}`;

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "F4") {
        event.preventDefault();
        handleBuy();
      } else if (event.key === "F6") {
        event.preventDefault();
        handleClear();
      } else if (event.key === "F7") {
        event.preventDefault();
        handleCancel();
      } else if (event.ctrlKey && event.key === "h") {
        event.preventDefault();
        if (isAdmin) handleSingleResult();
      } else if (event.ctrlKey && event.key === "r") {
        event.preventDefault();
        if (isAdmin) handleSingleReport();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleBuy, handleClear, handleCancel, handleSingleResult, handleSingleReport, isAdmin]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Top Bar */}
      <header className="bg-gradient-to-r from-red-800 to-red-900 text-white py-3 px-4 sm:px-6 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-4">
          <span className={cn("text-lg font-semibold", isMobileMenuOpen && "hidden sm:block")}>{headerText}</span>
        </div>
        <NavigationMenu isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen} isAdmin={isAdmin} />
      </header>

      {/* Scrolling Announcement Bar */}
      <ScrollingAnnouncementBar text={`🎯 Welcome to Three Circle 7 Lottery • Active Session: ${activeSession?.name || "None"} • Balance: ₹${balance} • Play Responsibly • Good Luck! •`} />

      {/* Main Content Area */}
      <main className="flex-1">
        <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg px-4">
          <GameInfoSection selectedGame={selectedGame} onGameChange={setSelectedGame} onGameTimeChange={setSelectedGameTime} activeSession={activeSession} sessions={sessions} />

          <BookingTable selectedGame={selectedGame} tableData={tableData} onInputChange={handleInputChange} selectedGameTime={selectedGameTime} activeSession={activeSession} />

          <ActionButtons selectedGame={selectedGame} tableData={tableData} onBuy={handleBuy} onClear={handleClear} onCancel={handleCancel} onSingleResult={handleSingleResult} onSingleReport={handleSingleReport} isAdmin={isAdmin} loading={loading} />
        </div>
      </main>

      <Toaster />
    </div>
  );
}
